import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import type { IntlShape } from 'react-intl';

import type { ChatItem, UserMessageItem } from '../../../contracts/harness.types';
import type {
  DesktopControlApprovalPort,
  DesktopControlLeaseSnapshot,
} from '../../../contracts/projects/desktop-control-approval.types';
import { isVisibleUserMessage } from '../../../data/projects/components/projectChatMessage.helpers';
import type { LiveDelegateStep } from '../../../data/projects/hooks/useProjectDesktopDelegation';
import { isClinicianActionableToolError } from '../../../data/projects/integrations/isClinicianActionableToolError';
import { clarificationAnswerDraftAtom } from '../../../data/projects/state/clarificationAnswerDraftAtom';
import { computeLatestTodoItemsById } from '../../../data/projects/timeline/agent-todo/computeLatestTodoItems';
import {
  isSuppressedTodoToolUse,
  type LatestTodoPlan,
} from '../../../data/projects/timeline/agent-todo/computeLatestTodoPlan';
import { deriveTimelinePresentation } from '../../../data/projects/timeline/presentation/derive-timeline-presentation';
import { createProjectTimelinePolicy } from '../../../data/projects/timeline/presentation/tool-axes';
import type { TimelinePolicy } from '../../../data/projects/timeline/presentation/types';
import type {
  ProjectItemRenderer,
  ProjectItemRendererDeps,
} from '../timeline/renderProjectItem';

/**
 * What the Work item renderer reads from the app, bound once at the composition
 * point: hooks, so each read stays in the component that renders the timeline.
 */
export interface ProjectItemRendererHooks {
  /**
   * The dispatcher with the app's half bound (Evidence's trail feed and cards,
   * the timeline's leaves, the approval hosts), and with the Evidence source
   * opener the app binds once per timeline. Its identity follows the opener's.
   */
  useCreateItemRenderer: () => (deps: ProjectItemRendererDeps) => ProjectItemRenderer;
  /** Gates the custom Nango connector cards: the integrations domain's flag. */
  useIsNangoIntegrationsEnabled: () => boolean;
  /** The compact, actionable Work source-citation chips. */
  useIsWorkSourceCitationChipsEnabled: () => boolean;
  /** The desktop-control lease the approval cards read, subscribed through the bridge's port. */
  desktopControl: Pick<DesktopControlApprovalPort, 'useLease'>;
}

interface UseProjectItemRendererArgs {
  items: ChatItem[];
  /** Owned by the caller so the card and the pinned summary share one object. */
  latestTodoPlan: LatestTodoPlan | null;
  agentRunId?: string;
  projectId: string;
  onOpenArtifact: ProjectItemRendererDeps['onOpenArtifact'];
  onResolveApproval: ProjectItemRendererDeps['onResolveApproval'];
  resolvingApproval: boolean;
  requireComputerUsePermissions?: ProjectItemRendererDeps['requireComputerUsePermissions'];
  desktopControlLease?: DesktopControlLeaseSnapshot | null;
  desktopTargetEnabled?: boolean;
  liveDelegateStep?: LiveDelegateStep;
  intl: IntlShape;
  shouldAnimateUserMessage: (item: UserMessageItem) => boolean;
  projectTitle?: string;
  routineRunWorkflowId?: string;
  turnInFlight?: boolean;
  viewedDocument?: ProjectItemRendererDeps['viewedDocument'];
  inlineReplyActions?: ProjectItemRendererDeps['inlineReplyActions'];
  loaderPhrases?: ProjectItemRendererDeps['loaderPhrases'];
}

const projectTimelinePolicy = (latestTodoPlan: LatestTodoPlan | null): TimelinePolicy =>
  createProjectTimelinePolicy({
    isVisibleUserMessage,
    isSuppressedTodo: (item) => isSuppressedTodoToolUse(item, latestTodoPlan),
    isSuppressedError: (item) => !isClinicianActionableToolError(item),
  });

export const useProjectItemRenderer = (
  {
    items,
    latestTodoPlan,
    agentRunId,
    projectId,
    onOpenArtifact,
    onResolveApproval,
    resolvingApproval,
    requireComputerUsePermissions,
    desktopControlLease,
    desktopTargetEnabled,
    liveDelegateStep,
    intl,
    shouldAnimateUserMessage,
    projectTitle,
    routineRunWorkflowId,
    turnInFlight,
    viewedDocument,
    inlineReplyActions,
    loaderPhrases,
  }: UseProjectItemRendererArgs,
  {
    useCreateItemRenderer,
    useIsNangoIntegrationsEnabled,
    useIsWorkSourceCitationChipsEnabled,
    desktopControl,
  }: ProjectItemRendererHooks
) => {
  const currentDesktopControlLease = desktopControl.useLease();
  const resolvedDesktopControlLease = desktopControlLease ?? currentDesktopControlLease;
  const isWorkSourceCitationChipsEnabled = useIsWorkSourceCitationChipsEnabled();
  const isNangoIntegrationsEnabled = useIsNangoIntegrationsEnabled();
  const clarificationAnswerDrafts = useAtomValue(clarificationAnswerDraftAtom);
  const createItemRenderer = useCreateItemRenderer();
  const presentation = useMemo(
    () => deriveTimelinePresentation(items, projectTimelinePolicy(latestTodoPlan)),
    [items, latestTodoPlan]
  );
  const latestTodoItemsById = useMemo(() => computeLatestTodoItemsById(items), [items]);
  const latestPersistentChildToolCallIds = useMemo(() => {
    const latestByChildRunId = new Map<string, string>();
    for (const item of items) {
      if (item.kind === 'tool_use' && item.childPersistent && item.childAgentRunId) {
        latestByChildRunId.set(item.childAgentRunId, item.id);
      }
    }
    return new Set(latestByChildRunId.values());
  }, [items]);
  const previousItemsById = useMemo(() => {
    const previousById = new Map<string, ChatItem>();
    for (let index = 1; index < items.length; index += 1) {
      const item = items[index];
      const previous = items[index - 1];
      if (item && previous) previousById.set(item.id, previous);
    }
    return previousById;
  }, [items]);

  return useMemo(() => {
    // Don't gate on agentRunId — preview bubbles render before a run exists.
    // Attachment URL fetch still requires a real run id (see AttachmentBlockRenderer).
    return createItemRenderer({
      items,
      previousItemsById,
      isWorkSourceCitationChipsEnabled,
      agentRunId: agentRunId ?? '',
      projectId,
      onOpenArtifact,
      onResolveApproval,
      resolvingApproval,
      requireComputerUsePermissions,
      liveDelegateStep,
      desktopControlLease: resolvedDesktopControlLease,
      desktopTargetEnabled,
      toolGroups: presentation.groups,
      workingBlocks: presentation.sections,
      turnInFlight: turnInFlight ?? false,
      latestTodoItemsById,
      latestTodoPlan,
      allowChildStreams: true,
      isNangoIntegrationsEnabled,
      latestPersistentChildToolCallIds,
      intl,
      shouldAnimateUserMessage,
      projectTitle,
      routineRunWorkflowId,
      clarificationAnswerDrafts,
      viewedDocument,
      inlineReplyActions,
      loaderPhrases,
    });
  }, [
    agentRunId,
    items,
    previousItemsById,
    isWorkSourceCitationChipsEnabled,
    projectId,
    onOpenArtifact,
    onResolveApproval,
    resolvingApproval,
    requireComputerUsePermissions,
    liveDelegateStep,
    resolvedDesktopControlLease,
    desktopTargetEnabled,
    presentation,
    turnInFlight,
    latestTodoItemsById,
    latestTodoPlan,
    latestPersistentChildToolCallIds,
    isNangoIntegrationsEnabled,
    intl,
    shouldAnimateUserMessage,
    projectTitle,
    routineRunWorkflowId,
    clarificationAnswerDrafts,
    viewedDocument,
    inlineReplyActions,
    loaderPhrases,
    createItemRenderer,
  ]);
};
